import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import { spotifyWithFirebaseService } from '../services/spotifyWithFirebase';
import { trackAudioPlay, trackClick } from '../utils/analytics';
import { getTrackMetadata, hasTrackMetadata } from '../data/trackMetadata';
import BandsintownWidget from '../components/BandsintownWidget';
import {
  MinimalGlobalStyle,
  Container,
  BackButton,
  Header,
  PageTitle,
  Bio,
  HeaderLinks,
  HeaderLink,
  Section,
  SectionTitle,
  StatsGrid,
  StatCell,
  StatValue,
  StatLabel,
  Tag,
  TagRow,
  CardLinks,
  CardLink,
} from '../components/minimal';

const MUSIC_ACCENT = '#22d3ee';

const WideInner = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 5rem 2rem 6rem;

  @media (max-width: 768px) {
    padding: 4rem 1.25rem 4rem;
  }
`;

const Search = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  color: #ffffff;
  font-family: inherit;
  font-size: 0.875rem;
  margin-bottom: 2rem;
  transition: border-color 0.2s ease, background 0.2s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    outline: none;
    border-color: ${MUSIC_ACCENT};
    background: rgba(255, 255, 255, 0.06);
  }
`;

const TrackList = styled.div`
  display: flex;
  flex-direction: column;
`;

const TrackRow = styled.button`
  display: grid;
  grid-template-columns: 56px 1fr auto auto;
  gap: 1rem;
  align-items: center;
  padding: 0.75rem 0;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  background: transparent;
  text-align: left;
  color: inherit;
  font: inherit;
  cursor: pointer;
  transition: background 0.15s ease;

  &:first-of-type {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }

  &:hover {
    background: rgba(255, 255, 255, 0.03);
  }
`;

const AlbumArt = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 4px;
  object-fit: cover;
  display: block;
`;

const TrackMain = styled.div`
  min-width: 0;
`;

const TrackName = styled.div`
  color: #ffffff;
  font-size: 0.95rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TrackAlbum = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.8125rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TrackBadge = styled.span<{ $kind: 'new' | 'details' }>`
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 0.2rem 0.5rem;
  border-radius: 3px;
  white-space: nowrap;
  ${p =>
    p.$kind === 'new'
      ? `color: ${MUSIC_ACCENT}; background: rgba(34, 211, 238, 0.12);`
      : `color: rgba(255,255,255,0.55); background: rgba(255,255,255,0.05);`}
`;

const TrackDuration = styled.div`
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.8125rem;
  font-variant-numeric: tabular-nums;
`;

const LoadMore = styled.div`
  text-align: center;
  padding: 2rem 0;
  color: rgba(255, 255, 255, 0.35);
  font-size: 0.8125rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Modal = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 2rem;
`;

const ModalContent = styled(motion.div)`
  background: #111113;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  max-width: 560px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  padding: 2rem;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  line-height: 1;

  &:hover {
    color: #ffffff;
  }
`;

const ModalArt = styled.img`
  width: 200px;
  height: 200px;
  object-fit: cover;
  border-radius: 8px;
  display: block;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 0.5rem;
`;

const ModalMeta = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.5rem 1rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.5);
  margin: 1rem 0 1.5rem;

  dt {
    color: rgba(255, 255, 255, 0.35);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 0.75rem;
  }

  dd {
    margin: 0;
    color: rgba(255, 255, 255, 0.75);
  }
`;

const PlayBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  margin-bottom: 1.5rem;
`;

const PlayButton = styled.button`
  background: ${MUSIC_ACCENT};
  color: #0a0a0a;
  border: none;
  border-radius: 4px;
  padding: 0.35rem 0.9rem;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    filter: brightness(1.1);
  }
`;

const Progress = styled.div`
  flex: 1;
  height: 3px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressBar = styled.div<{ $progress: number }>`
  width: ${p => p.$progress}%;
  height: 100%;
  background: ${MUSIC_ACCENT};
  transition: width 0.1s linear;
`;

const ProductionBlock = styled.div`
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const DownloadLink = styled.a`
  display: inline-block;
  margin-top: 0.5rem;
  color: ${MUSIC_ACCENT};
  font-size: 0.8125rem;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

interface Track {
  id: string;
  name: string;
  albumArt: string;
  albumName: string;
  releaseDate: string;
  previewUrl: string | null;
  spotifyUrl: string;
  duration_ms: number;
  artists?: Array<{ id: string; name: string }>;
}

const HIDDEN_SONGS = ['story', 'leave me alone'];

const musicPlatforms = [
  { name: 'Spotify', url: 'https://open.spotify.com/artist/7L3rQbwQVEGa71Ign85jyd' },
  { name: 'Apple Music', url: 'https://music.apple.com/us/artist/a5omic/1698709394' },
  { name: 'Amazon Music', url: 'https://music.amazon.com/artists/B0CCK25WRL/a5omic' },
  { name: 'YouTube', url: 'https://www.youtube.com/@A5omic_' },
  { name: 'Patreon', url: 'https://www.patreon.com/c/A5omic' },
  { name: 'Discord', url: 'https://discord.com/invite/GJJrgsh4Tx' },
];

const formatDuration = (ms: number): string => {
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const normalizeTrackName = (name: string): string =>
  name
    .toLowerCase()
    .replace(/'/g, '_')
    .replace(/[^a-z0-9\s]/g, ' ')
    .trim()
    .split(/\s+/)
    .join('_');

const getBackgroundBeatPath = (name: string) =>
  `/audio/beats/${normalizeTrackName(name)}.mp3`;

const checkFileExists = async (url: string): Promise<boolean> => {
  try {
    const res = await fetch(url, { method: 'HEAD' });
    const contentType = res.headers.get('content-type') || '';
    const isAudio = contentType.includes('audio/') || contentType.includes('mpeg');
    return res.status === 200 && isAudio;
  } catch {
    return false;
  }
};

const MusicPage: React.FC = () => {
  const [tracks, setTracks] = useState<Track[]>([]);
  const [displayedTracks, setDisplayedTracks] = useState<Track[]>([]);
  const [topTracks, setTopTracks] = useState<Track[]>([]);
  const [artistFollowers, setArtistFollowers] = useState<number | null>(null);
  const [selectedTrack, setSelectedTrack] = useState<Track | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [tracksPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [allTracksLoaded, setAllTracksLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [backgroundBeatExists, setBackgroundBeatExists] = useState(false);
  const [checkingBeat, setCheckingBeat] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const loadMoreObserverRef = useRef<IntersectionObserver | null>(null);

  const filterTracks = useCallback((list: Track[]) => {
    return list.filter(track => {
      const n = track.name.toLowerCase();
      if (HIDDEN_SONGS.some(h => n.includes(h))) return false;
      if (/\((?:ultra\s*)?slowed\)|sped\s*up/i.test(track.name)) return false;
      if (track.artists && track.artists.length > 1) return false;
      return true;
    });
  }, []);

  useEffect(() => {
    let mounted = true;
    spotifyWithFirebaseService
      .getArtistTopTracks('US')
      .then(data => {
        if (!mounted || !data?.length) return;
        const formatted = data.map(t => ({
          id: t.id,
          name: t.name,
          albumArt: t.album.images[0]?.url || '/profile.jpg',
          albumName: t.album.name,
          releaseDate: t.album.release_date,
          previewUrl: t.preview_url,
          spotifyUrl: `https://open.spotify.com/track/${t.id}`,
          duration_ms: t.duration_ms,
          artists: t.artists || [],
        }));
        setTopTracks(filterTracks(formatted));
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, [filterTracks]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const data = await spotifyWithFirebaseService.getArtistTracks();
        if (!mounted || !data?.length) return;
        const formatted = data.map(t => ({
          id: t.id,
          name: t.name,
          albumArt: t.album.images[0]?.url || '/profile.jpg',
          albumName: t.album.name,
          releaseDate: t.album.release_date,
          previewUrl: t.preview_url,
          spotifyUrl: `https://open.spotify.com/track/${t.id}`,
          duration_ms: t.duration_ms,
          artists: t.artists || [],
        }));
        setTracks(filterTracks(formatted));
      } catch (err) {
        console.error('Failed to load tracks:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [filterTracks]);

  useEffect(() => {
    let mounted = true;
    spotifyWithFirebaseService
      .getArtist()
      .then(a => {
        if (!mounted || !a) return;
        if (a.followers && typeof a.followers.total === 'number') {
          setArtistFollowers(a.followers.total);
        }
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const topTrackIds = useMemo(() => new Set(topTracks.map(t => t.id)), [topTracks]);

  const filteredTracks = useMemo(() => {
    let results = tracks.filter(t => !topTrackIds.has(t.id));
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      results = results.filter(
        t => t.name.toLowerCase().includes(q) || t.albumName.toLowerCase().includes(q),
      );
    }
    return results;
  }, [tracks, searchQuery, topTrackIds]);

  useEffect(() => {
    setDisplayedTracks(filteredTracks.slice(0, tracksPerPage));
    setCurrentPage(1);
    setAllTracksLoaded(filteredTracks.length <= tracksPerPage);
  }, [filteredTracks, tracksPerPage]);

  const loadMoreTracks = useCallback(() => {
    if (loadingMore || allTracksLoaded) return;
    setLoadingMore(true);
    const end = Math.min((currentPage + 1) * tracksPerPage, filteredTracks.length);
    setDisplayedTracks(filteredTracks.slice(0, end));
    setCurrentPage(p => p + 1);
    if (end >= filteredTracks.length) setAllTracksLoaded(true);
    setTimeout(() => setLoadingMore(false), 400);
  }, [currentPage, tracksPerPage, filteredTracks, loadingMore, allTracksLoaded]);

  useEffect(() => {
    loadMoreObserverRef.current = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          if (e.isIntersecting && !loadingMore && !allTracksLoaded) {
            loadMoreTracks();
          }
        });
      },
      { rootMargin: '200px', threshold: 0.1 },
    );
    return () => loadMoreObserverRef.current?.disconnect();
  }, [loadMoreTracks, loadingMore, allTracksLoaded]);

  const handleTrackClick = async (track: Track) => {
    setSelectedTrack(track);
    setBackgroundBeatExists(false);
    setCheckingBeat(true);
    const exists = await checkFileExists(getBackgroundBeatPath(track.name));
    setBackgroundBeatExists(exists);
    setCheckingBeat(false);
  };

  const handlePreviewToggle = async (track: Track) => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setAudioProgress(0);
      return;
    }
    try {
      const url = `/audio/previews/preview_${normalizeTrackName(track.name)}.mp3`;
      audioRef.current.src = url;
      audioRef.current.volume = 0.3;
      audioRef.current.ontimeupdate = () => {
        if (audioRef.current) {
          setAudioProgress(
            (audioRef.current.currentTime / audioRef.current.duration) * 100,
          );
        }
      };
      audioRef.current.onended = () => {
        setIsPlaying(false);
        setAudioProgress(0);
      };
      await audioRef.current.play();
      setIsPlaying(true);
      trackAudioPlay(track.name);
    } catch (err) {
      console.error('Error playing preview:', err);
      setIsPlaying(false);
    }
  };

  const handlePlatformClick = (platform: { name: string; url: string }) => {
    trackClick('music_platform', { platform_name: platform.name });
    window.open(platform.url, '_blank');
  };

  const mostRecentId = useMemo(() => {
    if (!tracks.length) return null;
    return [...tracks].sort(
      (a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime(),
    )[0].id;
  }, [tracks]);

  const renderTrack = (track: Track, isTop = false) => (
    <TrackRow key={`${isTop ? 'top-' : ''}${track.id}`} onClick={() => handleTrackClick(track)}>
      <AlbumArt src={track.albumArt} alt={track.name} loading="lazy" />
      <TrackMain>
        <TrackName>{track.name}</TrackName>
        <TrackAlbum>{track.albumName}</TrackAlbum>
      </TrackMain>
      {track.id === mostRecentId && !isTop ? (
        <TrackBadge $kind="new">NEW</TrackBadge>
      ) : hasTrackMetadata(track.name) ? (
        <TrackBadge $kind="details">DETAILS</TrackBadge>
      ) : (
        <span />
      )}
      <TrackDuration>{formatDuration(track.duration_ms)}</TrackDuration>
    </TrackRow>
  );

  return (
    <>
      <MinimalGlobalStyle $accent={MUSIC_ACCENT} />
      <Container>
        <BackButton />
        <WideInner>
          <Header>
            <PageTitle>A5omic</PageTitle>
            <Bio>
              AI-native music project. Some tracks are AI-generated, some are
              AI-assisted slowed + reverb edits, some are originals I've
              produced. 180k+ monthly Spotify listeners. Contact:{' '}
              <a href="mailto:atomicmbeats@gmail.com" style={{ color: MUSIC_ACCENT }}>
                atomicmbeats@gmail.com
              </a>
              .
            </Bio>
            <HeaderLinks>
              {musicPlatforms.map(p => (
                <HeaderLink
                  key={p.name}
                  href={p.url}
                  onClick={e => {
                    e.preventDefault();
                    handlePlatformClick(p);
                  }}
                >
                  {p.name}
                </HeaderLink>
              ))}
            </HeaderLinks>
          </Header>

          <StatsGrid>
            <StatCell>
              <StatValue>
                {artistFollowers ? new Intl.NumberFormat().format(artistFollowers) : '—'}
              </StatValue>
              <StatLabel>Spotify followers</StatLabel>
            </StatCell>
            <StatCell>
              <StatValue>{tracks.length || '—'}</StatValue>
              <StatLabel>Tracks</StatLabel>
            </StatCell>
            <StatCell>
              <StatValue>
                {tracks.length ? new Set(tracks.map(t => t.albumName)).size : '—'}
              </StatValue>
              <StatLabel>Releases</StatLabel>
            </StatCell>
          </StatsGrid>

          <Section>
            <SectionTitle>Shows</SectionTitle>
            <BandsintownWidget />
          </Section>

          {topTracks.length > 0 && (
            <Section>
              <SectionTitle>Top Tracks</SectionTitle>
              <TrackList>{topTracks.map(t => renderTrack(t, true))}</TrackList>
            </Section>
          )}

          <Section>
            <SectionTitle>All Tracks</SectionTitle>
            <Search
              type="text"
              placeholder="Search tracks…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {loading ? (
              <LoadMore>Loading music…</LoadMore>
            ) : (
              <>
                <TrackList>{displayedTracks.map(t => renderTrack(t))}</TrackList>
                {!allTracksLoaded && (
                  <LoadMore
                    ref={el => {
                      if (el && loadMoreObserverRef.current) {
                        loadMoreObserverRef.current.observe(el);
                      }
                    }}
                  >
                    {loadingMore ? 'Loading…' : 'Scroll for more'}
                  </LoadMore>
                )}
              </>
            )}
          </Section>
        </WideInner>
      </Container>

      <audio ref={audioRef} style={{ display: 'none' }} />

      <AnimatePresence>
        {selectedTrack && (
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedTrack(null)}
          >
            <ModalContent
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              onClick={e => e.stopPropagation()}
            >
              <CloseButton onClick={() => setSelectedTrack(null)}>×</CloseButton>
              <ModalArt src={selectedTrack.albumArt} alt={selectedTrack.name} />
              <ModalTitle>{selectedTrack.name}</ModalTitle>
              <ModalMeta as="dl">
                <dt>Album</dt>
                <dd>{selectedTrack.albumName}</dd>
                <dt>Released</dt>
                <dd>
                  {new Date(selectedTrack.releaseDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </dd>
                <dt>Duration</dt>
                <dd>{formatDuration(selectedTrack.duration_ms)}</dd>
              </ModalMeta>
              <PlayBar>
                <PlayButton onClick={() => handlePreviewToggle(selectedTrack)}>
                  {isPlaying ? 'Stop' : 'Preview'}
                </PlayButton>
                <Progress>
                  <ProgressBar $progress={audioProgress} />
                </Progress>
              </PlayBar>
              <CardLinks>
                <CardLink
                  href={selectedTrack.spotifyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Listen on Spotify &rarr;
                </CardLink>
              </CardLinks>

              {(() => {
                const metadata = getTrackMetadata(selectedTrack.name);
                const showProduction = metadata || (!checkingBeat && backgroundBeatExists);
                if (!showProduction) return null;
                return (
                  <ProductionBlock>
                    <SectionTitle>Production</SectionTitle>
                    {metadata?.baseSong && (
                      <ModalMeta as="dl">
                        <dt>Base</dt>
                        <dd>{metadata.baseSong}</dd>
                      </ModalMeta>
                    )}
                    {metadata?.effects && metadata.effects.length > 0 && (
                      <TagRow>
                        {metadata.effects.map((e, i) => <Tag key={i}>{e}</Tag>)}
                      </TagRow>
                    )}
                    {metadata?.notes && (
                      <ModalMeta as="dl">
                        <dt>Notes</dt>
                        <dd>{metadata.notes}</dd>
                      </ModalMeta>
                    )}
                    {backgroundBeatExists && (
                      <DownloadLink
                        href={getBackgroundBeatPath(selectedTrack.name)}
                        download={`${selectedTrack.name}_beat.mp3`}
                        onClick={() =>
                          trackClick('download_background_beat', {
                            track_name: selectedTrack.name,
                          })
                        }
                      >
                        Download background beat &rarr;
                      </DownloadLink>
                    )}
                    {metadata?.instrumentalFile && (
                      <DownloadLink
                        href={metadata.instrumentalFile}
                        download
                        onClick={() =>
                          trackClick('download_instrumental', {
                            track_name: selectedTrack.name,
                          })
                        }
                      >
                        Download instrumental &rarr;
                      </DownloadLink>
                    )}
                  </ProductionBlock>
                );
              })()}
            </ModalContent>
          </Modal>
        )}
      </AnimatePresence>
    </>
  );
};

export default MusicPage;
